import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DayJS from '../../helpers/DayJS'
import { useKeyboard } from '../../hooks/useKeyboard'
import { useOpenMeet } from '../../hooks/useOpenMeet'
import { setUserPresent } from '../../infrastructure/api-present'
import { setNewAccessToken } from '../../infrastructure/apis'
import { isProduction } from '../../interfaces/constants'
import { Icon } from '../Icon'
import { AssignationBar } from './AssignationBar'

/**
 * @see https://eggcooperation.atlassian.net/browse/EGG-872
 */
export const MeetButton = ({
    isInside,
    meetings,
    courseId,
    slug,
    profileId,
    nextClass,
    algorithmId,
    asyncCourse,
    offlineCourse,
    status,
    iconOnly,
    ...rest
}: ComponentProps<typeof AssignationBar> & { iconOnly?: boolean }) => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const { openMeet } = useOpenMeet(courseId)
    const [isInRange, setIsInRange] = useState(() => {
        if (!meetings || !nextClass) return false
        const now = DayJS()
        const hasGeneralMeetingInit = !!meetings.general
        const openTime = DayJS(hasGeneralMeetingInit ? nextClass.open : nextClass.start)
        const endTime = DayJS(nextClass.end)
        return now.isBetween(openTime, endTime)
    })

    const startSoon = DayJS(rest?.start).diff(DayJS(), 'hours') < 3 // User starts in less than 3 hours
    const hasGeneralMeeting = !!meetings?.general
    const hasMeeting = hasGeneralMeeting || !!meetings?.team
    const accessToken = rest['access-token'] // Extract for stable useCallback reference
    const requirePayment = rest.requirePayment

    // Update isInRange
    //
    // NO volver a suscribir el timeline acá. Este intervalo tenía además una promoción optimista
    // que reescribía la cache global ['Team', 'Timeline', courseId] cada segundo, desde una card:
    // una tarjeta de listado no debe mutar estado compartido a ese ritmo, y el ruteo de salas ya
    // no depende de eso (useOpenMeet lee currentEvent.room solo como pista en el click).
    // @see https://eggcooperation.atlassian.net/browse/EGG-3578
    useEffect(() => {
        const interval = setInterval(() => {
            if (!meetings || !nextClass) return

            const now = DayJS()
            const openTime = DayJS(hasGeneralMeeting ? nextClass?.open : nextClass?.start)
            const endTime = DayJS(nextClass.end)
            setIsInRange(now.isBetween(openTime, endTime))
        }, 1000)
        return () => clearInterval(interval)
    }, [hasGeneralMeeting, meetings, nextClass])

    // OCULTAR: condiciones donde el botón NO debe existir (sin posibilidad de acceso)
    const hideReasons = useMemo(() => {
        const reasons: string[] = []
        if (status === 'finished') reasons.push('curso finalizado')
        if (status === 'deserted') reasons.push('usuario inactivo')
        if (requirePayment) reasons.push('requiere pago')
        if (asyncCourse) reasons.push('curso asíncrono')
        if (offlineCourse) reasons.push('curso offline')
        return reasons
    }, [status, requirePayment, asyncCourse, offlineCourse])

    // DESHABILITAR: condiciones donde existe pero no se puede usar ahora
    // NOTA: canAccess está fuera del useMemo intencionalmente - es un cálculo simple (booleano)
    // y ya está incluido en las dependencias del useMemo
    const canAccess = status === 'studying' || status === 'unknown' || (status === 'enrolled' && startSoon)
    const disabledReasons = useMemo(() => {
        const reasons: string[] = []
        if (!hasMeeting) reasons.push('sin meetings configurados')
        if (!nextClass) reasons.push('sin nextClass programado')
        // NOTA: Solo evaluar isInRange cuando hasMeeting && nextClass existen,
        // porque si no hay meetings/nextClass, isInRange=false es redundante con las razones anteriores
        if (hasMeeting && nextClass && !isInRange) reasons.push(`fuera de horario (isInRange=${isInRange})`)
        if (!canAccess) reasons.push(`status no permitido (status=${status}, startSoon=${startSoon})`)
        return reasons
    }, [hasMeeting, nextClass, isInRange, canAccess, status, startSoon])

    const disabled = disabledReasons.length > 0
    const isHidden = hideReasons.length > 0

    // Debug logging - only log when state changes to avoid spam from interval re-renders
    const prevStateRef = useRef<{ hidden: boolean; disabled: boolean; reasons: string }>()
    useEffect(() => {
        if (isProduction) return

        const currentReasons = isHidden ? hideReasons.join(', ') : disabledReasons.join(', ')
        const currentState = { hidden: isHidden, disabled, reasons: currentReasons }
        const prevState = prevStateRef.current

        // Only log if state changed
        if (!prevState || prevState.hidden !== isHidden || prevState.disabled !== disabled || prevState.reasons !== currentReasons) {
            if (isHidden) {
                console.info(`[MeetButton] hidden for ${courseId}:`, hideReasons.join(', '))
            } else if (disabled) {
                console.info(`[MeetButton] disabled for ${courseId}:`, disabledReasons.join(', '))
            }
            prevStateRef.current = currentState
        }
    }, [isHidden, disabled, hideReasons, disabledReasons, courseId])

    // Click handler - memoized for stable reference in keyboard shortcuts
    const onClick = useCallback(() => {
        if (disabled) return
        setNewAccessToken(accessToken)
        setUserPresent(courseId)
        const baseUrl = `/${slug ?? courseId}`
        const profileParam = profileId ? `&profileId=${profileId}` : ''
        if (isInside) return openMeet()
        return navigate(`${baseUrl}?meet=true${profileParam}`)
    }, [disabled, accessToken, courseId, slug, profileId, isInside, openMeet, navigate])

    // Handle keyboard shortcuts - only register when inside course and not hidden
    // DECISIÓN: No registrar shortcut en Home (!isInside) para evitar handler vacío
    const keyboardShortcuts = useMemo(
        () =>
            isHidden || !isInside
                ? []
                : [
                      {
                          key: 'l',
                          onKeyPressed: onClick,
                      },
                  ],
        [isHidden, isInside, onClick]
    )
    useKeyboard(keyboardShortcuts)

    if (isHidden) {
        return null
    }

    // DECISIÓN UX: Solo mostrar hint "(l)" cuando el shortcut funciona (isInside=true)
    // No mostrar en Home porque el shortcut no está disponible ahí
    const buttonTitle = t('Live') + (isInside ? ' (l)' : '')

    return (
        <button className="primary custom-action" onClick={onClick} disabled={disabled} title={buttonTitle}>
            <Icon icon={['fas', 'circle-video']} size="lg" className="button-icon" />
            {!iconOnly && 'LIVE'}
        </button>
    )
}
